import React, { useState } from 'react';
import { useSignatureDiagnostic } from '../../hooks/useSignatureDiagnostic';

interface SignatureDiagnosticPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignatureDiagnosticPanel: React.FC<SignatureDiagnosticPanelProps> = ({
  isOpen,
  onClose
}) => {
  const [signature, setSignature] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [message, setMessage] = useState('');
  
  const { diagnoseSignature, isLoading, lastDiagnostic, clearDiagnostic } = useSignatureDiagnostic();

  const handleDiagnose = async () => {
    if (!signature.trim() || !walletAddress.trim() || !message.trim()) {
      alert('Veuillez remplir tous les champs');
      return;
    }
    
    await diagnoseSignature(signature.trim(), walletAddress.trim(), message.trim());
  };

  const handleReset = () => {
    setSignature('');
    setWalletAddress('');
    setMessage('');
    clearDiagnostic();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="cosmic-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-emerald-100">
              🔍 Diagnostic de Signature
            </h2>
            <button
              onClick={onClose}
              className="text-emerald-300 hover:text-emerald-100 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-emerald-200 mb-2">
                Signature (base58)
              </label>
              <textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Collez votre signature base58 ici..."
                className="w-full p-3 rounded-lg bg-gray-800/50 border border-emerald-500/30 text-emerald-100 placeholder-emerald-300/50 resize-none h-24"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-emerald-200 mb-2">
                Adresse Wallet
              </label>
              <input
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="6jm9c35VgtM3DP2LmHc7ij36SwYA4ADdE9ZicUbfVxCD"
                className="w-full p-3 rounded-lg bg-gray-800/50 border border-emerald-500/30 text-emerald-100 placeholder-emerald-300/50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-emerald-200 mb-2">
                Message Signé
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Collez le message qui a été signé..."
                className="w-full p-3 rounded-lg bg-gray-800/50 border border-emerald-500/30 text-emerald-100 placeholder-emerald-300/50 resize-none h-24"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleDiagnose}
                disabled={isLoading}
                className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Diagnostic...</span>
                  </>
                ) : (
                  <>
                    <span>🔍</span>
                    <span>Diagnostiquer</span>
                  </>
                )}
              </button>

              <button
                onClick={handleReset}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700"
              >
                🔄 Reset
              </button>
            </div>
          </div>

          {/* Results */}
          {lastDiagnostic && (
            <div className="space-y-4">
              <div className="border-t border-emerald-500/30 pt-6">
                <h3 className="text-lg font-semibold text-emerald-100 mb-4">
                  {lastDiagnostic.isValid ? '✅ Résultat' : '❌ Résultat'}
                </h3>

                {/* Status */}
                <div className={`p-4 rounded-lg mb-4 ${
                  lastDiagnostic.isValid 
                    ? 'bg-green-900/30 border border-green-500/50' 
                    : 'bg-red-900/30 border border-red-500/50'
                }`}>
                  <div className="font-medium">
                    {lastDiagnostic.isValid ? (
                      <span className="text-green-300">✅ Signature valide</span>
                    ) : (
                      <span className="text-red-300">❌ Signature invalide</span>
                    )}
                  </div>
                </div>

                {/* Errors */}
                {lastDiagnostic.errors.length > 0 && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-red-300 mb-2">❌ Erreurs:</h4>
                    <ul className="space-y-1">
                      {lastDiagnostic.errors.map((error, index) => (
                        <li key={index} className="text-red-200 text-sm">
                          • {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {lastDiagnostic.warnings.length > 0 && (
                  <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-orange-300 mb-2">⚠️ Avertissements:</h4>
                    <ul className="space-y-1">
                      {lastDiagnostic.warnings.map((warning, index) => (
                        <li key={index} className="text-orange-200 text-sm">
                          • {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {lastDiagnostic.recommendations.length > 0 && (
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-300 mb-2">💡 Recommandations:</h4>
                    <ul className="space-y-1">
                      {lastDiagnostic.recommendations.map((rec, index) => (
                        <li key={index} className="text-blue-200 text-sm">
                          • {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Detailed Report */}
                <div className="bg-gray-800/30 border border-gray-500/30 rounded-lg p-4">
                  <h4 className="font-medium text-gray-300 mb-2">📋 Rapport Détaillé:</h4>
                  <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-x-auto">
                    {lastDiagnostic.report}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
